// main.ts — boot the Day-0 rig: load the dictionary, mount the empty Compliance Window, register
// every effect off the bus, wire the tuning panel + capture tools, and (if asked) run a seeded
// replay. No gameplay lives here — this is the rig proving itself end to end.

import "./styles.css";
import { loadDictionary } from "./core/dictionary";
import { mountComplianceWindow } from "./ui/compliance-window";
import { registerAllEffects } from "./juice/effects";
import { initFxLayer } from "./juice/fxlayer";
import { mountTuner, revealRails, getRails, tutorialEnabled } from "./tuner";
import { mountFunPanel } from "./ui/funpanel";
import { initTooltips } from "./ui/tooltip";
import { startIntro } from "./ui/intro";
import { isPhoneLayout, initMobileShell } from "./ui/mobile-shell";
import { createMashHint, createRailHint, createBoxHint } from "./ui/hint";
import { initReduceMotion } from "./juice/a11y";
import { setPersonalityBig, armShowcase } from "./juice/effects/09-personality";
import { setCalm, isCalm } from "./juice/a11y";
import { fireOnce } from "./tuner/fire";
import { setJuiceSpeed } from "./tuner/scale";
import { startRecording, stopRecording, frameStrip } from "./juice/capture";
import { maybeRunReplay } from "./core/replay";
import { SEED } from "./core/rng";
import { bus } from "./core/bus";

const app = document.querySelector<HTMLDivElement>("#app")!;

async function boot() {
  app.innerHTML = `<pre class="boot">DULY NOTED — loading dictionary…</pre>`;

  const meta = await loadDictionary();
  console.log(
    `%c[dict] ${meta.mode} · ${meta.words.toLocaleString()} words loaded · ${meta.slursStripped} slurs stripped · ${meta.profanityFlagged} profanity flags · seed=${SEED}`,
    "color:#d99a17",
  );

  // Order: fx canvas first (particles draw onto it), then the window, then the effects that
  // subscribe to the events the window emits.
  initReduceMotion(); // apply the OS prefers-reduced-motion default to <body>
  initFxLayer();
  const cw = mountComplianceWindow(app);
  const submit = cw.submit;
  registerAllEffects();
  mountTuner(submit); // version-switchable tuning panel (v1.0 Tweakpane ↔ v1.1 double/halve)
  const mashPanel = mountFunPanel(app); // "mash to play" front door — a landscape rectangle below the window
  initTooltips(); // delayed hover tips on [data-tip] controls

  // Staged reveal: START MASHING drops in after the welcome; the rails then the input follow as the
  // player engages. See ui/intro.ts for the choreography + timings.
  //
  // A/B experiment: A (default) = plain reveal, no prompts. B = the handwritten tutorial — a themed
  // scribble + arrow at each phase (mash → rails → type-a-word), each wiped on the next fire.
  if (isPhoneLayout()) {
    // "C" mobile layout: no desktop staged choreography, tutorial kept warm (off here — see NEXT).
    // Reveal the real nodes, then reorganize them into the swipe/tab panels.
    mashPanel.classList.remove("pre-reveal");
    const rails = getRails();
    rails?.left.classList.remove("pre-reveal-reserve");
    rails?.right.classList.remove("pre-reveal-reserve");
    // Reveal the type field WITHOUT the desktop stamp's auto-focus — on a phone that would pop the
    // keyboard on load and steal the first tab tap. Just un-hide it.
    document.getElementById("cw-input-wrap")?.classList.remove("cw-pre-stamp");
    initMobileShell();
  } else if (tutorialEnabled()) {
    const mashHint = createMashHint();
    mashPanel.appendChild(mashHint.el);
    const rails = getRails();
    const persHint = rails ? createRailHint("pers", rails.left) : null;
    const juiceHint = rails ? createRailHint("juice", rails.right) : null;
    const inputWrap = document.querySelector<HTMLElement>("#cw-input-wrap");
    const boxHint = inputWrap ? createBoxHint(inputWrap) : null;
    startIntro({
      mashPanel,
      revealRails: () => revealRails({ onLeft: persHint?.reveal, onRight: juiceHint?.reveal }),
      stampInput: () => {
        cw.stampInput();
        window.setTimeout(() => boxHint?.reveal(), 1400); // let the 460ms stamp fully settle before we measure the field
      },
      showMashHint: mashHint.reveal,
      hideMashHint: mashHint.hide,
      hideRailHints: () => {
        persHint?.hide();
        juiceHint?.hide();
      },
      hideBoxHint: () => boxHint?.hide(),
    });
  } else {
    startIntro({ mashPanel, revealRails, stampInput: cw.stampInput });
  }

  // Welcome dance: auto-fires ~0.5s after load (Jason's pick). It is SILENT until the first
  // interaction — the browser won't start audio before a user gesture — then sound is available for
  // everything after. (Big mode is the default now; no need to toggle it here.)
  window.setTimeout(() => {
    const prevCalm = isCalm();
    setCalm(true); // welcome is NON-triggering: no whole-screen shake (restore the user's setting after)
    armShowcase(true, 2); // flirt reel among the rare tiles (the phrase has no epics)
    cw.showPhrase("WELCOME\nPLAYER #1", "Welcome, Player #1. ♪"); // \n = forced line break
    setCalm(prevCalm);
  }, 500);

  // Expose deterministic hooks for the /feel loop + Chrome MCP (drive headlessly, capture frames).
  (window as any).__juice = {
    submit,
    bus,
    seed: SEED,
    startRecording,
    stopRecording,
    frameStrip,
    fire: fireOnce, // fire a random valid test word (same path the tuner uses)
    setJuiceSpeed, // let a capture harness slow the whole rig for smooth frame grabs
    welcome: () => {
      const prevCalm = isCalm();
      setCalm(true); // non-triggering like the real auto-welcome (no whole-screen shake)
      setPersonalityBig(true);
      armShowcase(true, 2);
      cw.showPhrase("WELCOME\nPLAYER #1", "Welcome, Player #1. ♪"); // \n = the two-line startup layout
      setCalm(prevCalm);
    },
    fireValid: (word = "compliant") =>
      bus.emit("word:valid", {
        word,
        tiles: [...document.querySelectorAll<HTMLElement>(".tile")],
        flagged: false,
        at: performance.now(),
      }),
  };

  // ?replay=demo (+ ?seed=) → reproducible scripted run for capture / A-B.
  maybeRunReplay(submit);
}

boot().catch((err) => {
  console.error(err);
  app.innerHTML = `<pre class="boot">boot failed: ${String(err)}</pre>`;
});
